import { pluralize } from "../format/word";
import { formatDeliveryTimeRange, extractDayFromDate, shouldShowTimeRange } from "../format/date";

/**
 * Calculates time remaining until a given timestamp
 * @param {string} timestamp - ISO timestamp string
 * @returns {string|null} Formatted time remaining (e.g., "2 hours 15 minutes")
 */
export const calculateTimeRemaining = timestamp => {
  if (!timestamp) return null;

  const cutoffTime = new Date(timestamp);
  const now = new Date();
  const diffMs = cutoffTime - now;

  if (diffMs <= 0) return null;

  const diffMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${pluralize(hours, "hour")} ${pluralize(minutes, "minute")}`;
  }
  if (hours > 0) {
    return pluralize(hours, "hour");
  }
  return pluralize(minutes, "minute");
};

/**
 * Updates timeleft display based on delivery date
 * @param {HTMLElement} $timeleft - The timeleft element
 * @param {boolean} isToday - Whether the delivery is today
 * @param {string} timeleftTimestamp - ISO timestamp string
 */
export const updateTimeleftDisplay = ($timeleft, isToday, timeleftTimestamp) => {
  if (!$timeleft) return;

  if (isToday && timeleftTimestamp) {
    const timeRemaining = calculateTimeRemaining(timeleftTimestamp);
    if (timeRemaining) {
      // eslint-disable-next-line no-param-reassign
      $timeleft.innerHTML = `Order in <strong>${timeRemaining}</strong>`;
      $timeleft.classList.remove("hidden");
      return;
    }
  }
  $timeleft.classList.add("hidden");
};

/**
 * Formats delivery date text based on delivery timing
 * @param {string} date - The delivery date string
 * @param {Object} options - Formatting options
 * @param {string} options.deliveryTime - Start time for delivery window
 * @param {string} options.deliveryTimeEnd - End time for delivery window
 * @param {boolean} options.includeByForTimeRange - Whether to include "by" before time range
 * @returns {string} Formatted delivery date text
 */
export const formatDeliveryDateText = (date, options = {}) => {
  const { deliveryTime, deliveryTimeEnd, includeByForTimeRange = false } = options;
  const lowerDate = date.toLowerCase();
  const isToday = lowerDate.includes("today");
  const isTomorrow = lowerDate.includes("tomorrow");
  const isTimeRangeAvailable = shouldShowTimeRange(date) && deliveryTime && deliveryTimeEnd;
  const timeRange = formatDeliveryTimeRange(deliveryTime, deliveryTimeEnd);

  if (isToday) {
    if (isTimeRangeAvailable && includeByForTimeRange) {
      return `Today by ${timeRange}`;
    }
    return "Today";
  }

  if (isTomorrow) {
    if (isTimeRangeAvailable && includeByForTimeRange) {
      return `Tomorrow by ${timeRange}`;
    }
    return "Tomorrow";
  }

  if (isTimeRangeAvailable) {
    return `${extractDayFromDate(date)} ${timeRange}`;
  }
  return date;
};
